import { animate, state, style, transition, trigger } from '@angular/animations';
import { ChangeDetectorRef, Component, HostListener, Inject, ViewChild, SecurityContext, ElementRef, NgZone } from '@angular/core';
import { Firestore, collectionData, docData, collection, doc, query, where, deleteDoc } from '@angular/fire/firestore';
import { FormControl, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTooltip } from '@angular/material/tooltip';
import { Title, DomSanitizer, SafeHtml, SafeResourceUrl } from '@angular/platform-browser';
import { faCopy } from '@fortawesome/free-regular-svg-icons';
import { faExternalLink } from '@fortawesome/free-solid-svg-icons';
import { GoogleTagManagerService } from 'angular-google-tag-manager';
import { combineLatest, map, Observable, shareReplay, Subscription } from 'rxjs';
import { ClipboardService } from 'src/app/shared/services/clipboard.service';
import { DateService } from 'src/app/shared/services/date.service';
import { environment } from 'src/environments/environment';
import { ClearscapeService } from '../../clearscape.service';
import { AuthService } from '../../shared/services/auth.service';
import { containsOnlyAllowedCharacters, doesntEndWithHyphen, startsWithLetter } from './validators';
import { IEnvironment } from 'src/app/shared/services/environment.types';
import { ProfileType } from '../../shared/services/profile-type';
import { ProfileCompletionComponent } from '../profile-completion/profile-completion.component';
import { ProfileTypeService } from '../../shared/services/profile-type.service';
import { User } from 'src/app/shared/services/user';
import { Timestamp } from 'firebase/firestore';
import { RegionDetectionService } from '../../shared/services/region-detection.service';
import { PendoService } from '../../services/pendo.service';
import { JupyterDemosService, JupyterDemo, DemoLink, FilterCategory } from '../../shared/services/jupyter-demos.service';
import { AiChatService, ChatResponse } from 'src/app/services/ai-chat.service';
import { HttpClient } from '@angular/common/http';
const sleepBeforeRefresh = 8000;

export interface CreateEnvironmentData {
  environmentName: string;
  environmentPassword: string;
  region: string
}
export interface Notification {
  id: string;
  title: string;
  text: string;
  type: string;
  environment: string;
  suggestedAction: string;
}

export class Consumption {
  currentConsumption:number;
  maxCredits: number;
  resetDate: Date;
  constructor(currentConsumption: number | string, maxCredits: number, resetDate: Timestamp) {
    this.currentConsumption = typeof currentConsumption === 'string' ? parseFloat(currentConsumption) : currentConsumption;
    this.maxCredits = maxCredits;
    this.resetDate = resetDate.toDate();
    console.log(`resetAt: ${this.resetDate}`)
  }

  get percentage(): number {
    return this.currentConsumption / this.maxCredits * 100;
  }
}

const NOTIFICATION_TYPE_ICONS: Map<string,string> = new Map<string,string>([
  ['INFO', 'info'],
  ['WARNING', 'warning'],
  ['ALERT', 'info'],
  ['DANGER', 'cancel']
]);

export interface Utilization {
  currentUtilization: number;
  billingRestartDate: Date;
  budget: number;
}

export interface UtilizationUser extends User {
  utilization: Utilization
  currentConsumption: number;
}

interface FilterDropdownConfig {
  key: string;
  label: string;
  options: string[];
}

@Component({
  standalone: false,
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss'],
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({ height: '0px', minHeight: '0' })),
      state('expanded', style({ height: '*' })),
      transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
    ]),
  ],
})
export class DashboardComponent {
  @ViewChild('dnsTooltip') dnsTooltip!: MatTooltip;
  @ViewChild('userTooltip') userTooltip!: MatTooltip;
  @ViewChild('chatMessagesContainer') chatMessagesContainer!: ElementRef;
  title = 'Trial';
  notifications: Observable<Notification[]>;
  notificationsSubscription: Subscription;
  notificationsCache: Notification[] = [];
  notificationsByEnvironment: Map<string, Notification[]> = new Map();
  user: Observable<User| undefined>
  consumption: Observable<Consumption|undefined>;
  profileType: Observable<ProfileType | undefined>;
  focused: boolean = true;


  displayedColumns = ['name', 'region', 'state', 'host', 'actions', 'expand'];
  columnsToDisplayWithExpand = [...this.displayedColumns, 'expand'];

  selectedEnvironment: IEnvironment | undefined;
  environments: Observable<IEnvironment[]>;
  environmentsSubscription: Subscription;
  notificationTypeIcons = NOTIFICATION_TYPE_ICONS;

  currentTime = new Date();
  private countdownInterval: any;
  isLoadingResults: boolean = false;
  token: string = '';
  faCopy = faCopy;
  faExternalLink = faExternalLink;
  createEnvironmentData: CreateEnvironmentData = {
    environmentName: '',
    environmentPassword: '',
    region: 'us-central'
  };

  // Carousel properties
  carouselItems: { image: string; link: string; type?: 'image' | 'video'; thumbnail?: string }[] = [];
  currentCarouselIndex: number = 0;
  carouselInterval: any;
  carouselLoading: boolean = true;
  // Index of the video ad currently playing inline, or null when none.
  playingCarouselIndex: number | null = null;
  // Video currently expanded into the pop-up overlay, or null when none.
  expandedVideo: { image: string; link: string; type?: 'image' | 'video'; thumbnail?: string } | null = null;
  private readonly carouselMinHeightPx = 750;
  showCarousel: boolean = this.shouldShowCarousel();

  // Demos properties
  allDemos: JupyterDemo[] = [];
  filteredDemos: JupyterDemo[] = [];
  displayedDemos: JupyterDemo[] = [];
  demosLoading: boolean = true;
  filtersLoading: boolean = true;
  isNotebookViewerOpen: boolean = false;
  notebookViewerUrl: SafeResourceUrl | null = null;
  notebookViewerTitle: string = '';
  subjectFilterConfigs: FilterDropdownConfig[] = [];
  filters: Record<string, string> = {
    availability: '' // 'preview' or 'environment' or ''
  };
  filterOptions = {
    availabilities: [
      { value: 'preview', label: 'Preview Available' },
      { value: 'environment', label: 'Environment Required' }
    ]
  };

  // AI Chat properties
  chatMessage: string = '';
  chatMessages: Array<{role: 'user' | 'assistant', content: string, sourceDocuments?: string[]}> = [];
  isChatCollapsed: boolean = false;
  isChatLoading: boolean = false;
  isChatHistoryLoading: boolean = false;
  chatSessionId: string | null = null;
  chatError: string | null = null;
  aiFilterActive: boolean = false;
  aiFilteredNotebooks: string[] = [];

  constructor(
    private dialog: MatDialog,
    private titleService: Title,
    private clearscapeService: ClearscapeService,
    private snackBar: MatSnackBar,
    public authService: AuthService,
    public firestore: Firestore,
    public dateService: DateService,
    private clipboardService: ClipboardService,
    private gtmService: GoogleTagManagerService,
    private profileTypeService: ProfileTypeService,
    private pendoService: PendoService,
    private jupyterDemosService: JupyterDemosService,
    private aiChatService: AiChatService,
    private sanitizer: DomSanitizer,
    private http: HttpClient,
    private cdr: ChangeDetectorRef,
    private ngZone: NgZone
  ) {

    this.notifications = collectionData<Notification>(
      collection(firestore, `users/${authService.userData.email}/notifications`) as any,
      { idField: 'id' }
    );
    this.environments = collectionData<IEnvironment>(
      query(collection(firestore, 'environments'), where('owner', '==', authService.userData.email)) as any
    ).pipe(shareReplay({ bufferSize: 1, refCount: false }));
    this.user = docData<User>(doc(firestore, `users/${authService.userData.email}`) as any);
    this.profileType = this.profileTypeService.getProfileTypes(this.user);

    // Subscribe to user data and update Pendo visitor ID when available
    this.user.subscribe((userData) => {
      if (userData && (userData as any).id) {
        this.pendoService.updateVisitorId((userData as any).id);
      }
    });

    this.consumption = combineLatest([this.user, this.profileType]).pipe(map(([user, profileType]) => {
      if (user && profileType) {
        return new Consumption(user.currentConsumption, profileType.maxCredits, user.resetDate);
      }
      return undefined;
    }))
    this.environmentsSubscription = this.environments.subscribe((envs: IEnvironment[]) => {
      console.log(`env list has changed`);

      if (!this.selectedEnvironment) {
        if (envs.length) {
          this.selectedEnvironment = envs[0];
        }
      } else {
        if (!envs.find(e => e.name === this.selectedEnvironment?.name)){
          if (envs.length > 0) {
            this.selectedEnvironment = envs[0];
          } else {
            this.selectedEnvironment = undefined;
          }
        } else {
          this.selectedEnvironment = envs.find(e => e.name === this.selectedEnvironment?.name);
        }
      }
    });

    this.notificationsSubscription = this.notifications.subscribe(
      (notifications: Notification[]) => {
        this.notificationsCache = notifications;
        const map = new Map<string, Notification[]>();
        for (const n of notifications) {
          if (n.environment) {
            const list = map.get(n.environment) ?? [];
            list.push(n);
            map.set(n.environment, list);
          }
        }
        this.notificationsByEnvironment = map;
        if (notifications.length > 0 && !this.focused) {
          this.onHandleNotificationReceived(notifications.length);
        }
      }
    )
  }

  ngOnInit() {

    this.titleService.setTitle(this.title);
    const token = localStorage.getItem('token');
    if (token) {
      this.setToken(token);
    }

    this.checkAndShowProfileCompletion();

    // Load carousel items
    if (this.showCarousel) {
      this.loadCarouselItems();
    } else {
      this.carouselLoading = false;
    }

    // Load demos
    this.loadDemos();
    
    // Load chat history if session exists
    this.loadChatHistory();
    
    // Start countdown timer for tera-ui service availability
    this.ngZone.runOutsideAngular(() => {
      this.countdownInterval = setInterval(() => {
        this.ngZone.run(() => {
          this.currentTime = new Date();
          this.cdr.detectChanges();
        });
      }, 1000);
    });
    (window as any).applyAiFilterFromMessage = (sourceDocsJson: string) => {
      try {
        const sourceDocuments = JSON.parse(sourceDocsJson);
        console.log('Global filter function called with:', sourceDocuments);
        this.applyAiFilter(sourceDocuments);
      } catch (error) {
        console.error('Error applying AI filter:', error);
      }
    };
  }

  ngOnDestroy() {
    if (this.notificationsSubscription) {
      this.notificationsSubscription.unsubscribe();
    }
    if (this.environmentsSubscription) {
      this.environmentsSubscription.unsubscribe();
    }
    // Stop carousel when component is destroyed
    this.stopCarousel();
    // Clear countdown interval
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
    }
  }



  @HostListener('window:focus')
  onFocus() {
    this.focused = true;
    this.titleService.setTitle('Trial');
  }

  @HostListener('window:blur')
  onBlur() {
    this.focused = false;
  }

  @HostListener('window:resize')
  onResize() {
    const shouldShowCarousel = this.shouldShowCarousel();
    this.showCarousel = shouldShowCarousel;

    if (!shouldShowCarousel) {
      this.stopCarousel();
    } else if (!this.carouselLoading) {
      if (this.carouselItems.length > 0) {
        this.startCarousel();
      } else {
        this.loadCarouselItems();
      }
    }
  }

  onHandleNotificationReceived(amount: Number) {
    this.titleService.setTitle(`(${amount}) Trial`);
  }

  openRestApiDocs(): void {
    window.open((environment.name === 'production') ? 'https://api.trial.teradata.com/api-docs' : `https://api.${environment.name}.trial.teradata.com/api-docs`, "_blank");
  }

  openAiStudio(): void {
    window.open('https://tdicam53475pp54.private.preprod.cloud.teradata.com/one-td/', '_blank');
  }

  copyText(text: string, name: string) {
    if (name === 'dns' || name === 'mcp') {
      this.clipboardService.copyToClipboard(text, this.dnsTooltip);
    }

    if (name === 'user') {
      this.clipboardService.copyToClipboard(text, this.userTooltip);
    }
  }

  copyToken() {
    this.clipboardService.copyToClipboard(localStorage.getItem("token")!, this.userTooltip);
  }

  stopEnvironment(environmentName: string) {
    this.clearscapeService.stop(this.token, environmentName).subscribe({
      next: (response: any) => {
        this.handleResponse(response);
      },
      error: (e) => {
        this.handleError(e);
      }
    });
    this.isLoadingResults = true;
    this.sleep(sleepBeforeRefresh).then(() => {
      this.isLoadingResults = false;
    });
  }

  requestOAF() {
    const environmentName = this.selectedEnvironment!.name;
    this.clearscapeService.requestOAF(this.token, environmentName).subscribe({
      next: (response: any) => {
        this.handleResponse(response);
      },
      error: (e) => {
        this.handleError(e);
      }
    });
    this.isLoadingResults = true;
    this.sleep(sleepBeforeRefresh).then(() => {
      this.isLoadingResults = false;
    });
  }

  startEnvironment(environmentName: string) {
    this.clearscapeService.start(this.token, environmentName).subscribe({
      next: (response: any) => {
        this.handleResponse(response);
      },
      error: (e) => {
        this.handleError(e);
      }
    });
    this.isLoadingResults = true;
    this.sleep(sleepBeforeRefresh).then(() => {
      this.isLoadingResults = false;
    });
  }

  deleteEnvironment(environmentName: string) {
    this.clearscapeService.delete(this.token, environmentName).subscribe({
      next: (response: any) => {
        this.handleResponse(response);
      },
      error: (e) => {
        this.handleError(e);
      }
    });
    this.isLoadingResults = true;
    this.sleep(sleepBeforeRefresh).then(() => {
      this.isLoadingResults = false;
    });
  }

  confirmDeleteEnvironment(environmentName: string) {
    const dialogRef = this.dialog.open(DeleteEnvironmentDialogComponent, {
      width: '500px',
      data: { environmentName }
    });

    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.deleteEnvironment(environmentName);
      }
    });
  }

  createEnvironment(createEnvironmentData: CreateEnvironmentData) {
    this.clearscapeService.create(this.token, createEnvironmentData.environmentName,
      createEnvironmentData.environmentPassword, createEnvironmentData.region).subscribe({
        next: (response: any) => {
          this.gtmService.pushTag({
            event: 'web_create_env_success'
          })
          this.handleResponse(response);
        },
        error: (e) => {
          this.handleError(e);
        }
      });
    this.isLoadingResults = true;
    this.sleep(sleepBeforeRefresh).then(() => {
      this.isLoadingResults = false;
    });
  }

  setToken(token: string) {
    this.token = token;
  }

  createEnvironmentSubmitted(createEnvironmentData: CreateEnvironmentData) {
    this.createEnvironment(createEnvironmentData);
  }

  sleep(ms: number) {
    return new Promise((resolve) => {
      setTimeout(resolve, ms);
    });
  }

  handleError(e: any) {
    console.log('handleError');
    console.log(e);
    this.isLoadingResults = false;
    this.snackBar.open((e.error && e.error.message) ? e.error.message : e.error, 'OK');
  }

  handleResponse(response: any) {
    console.log('handleResponse');
    console.log(JSON.stringify(response, null, 2));
  }

  showCreateEnvironmentDialog() {
    this.gtmService.pushTag({
      event: 'web_create_env_dialog_show'
    })
    const dialogRef = this.dialog.open(CreateEnvironmentDialogComponent, {
      data: this.createEnvironmentData,
      width: '600px'
    });
    dialogRef.afterClosed().subscribe(result => {
      console.log('The dialog was closed with result: ' + JSON.stringify(result));
      if (result) {
        this.createEnvironmentData.environmentName = result.environmentName;
        this.createEnvironmentData.environmentPassword = result.environmentPassword;
        this.createEnvironmentData.region = result.region;
        this.createEnvironment(this.createEnvironmentData);
        this.gtmService.pushTag({
          event: 'web_create_env_init'
        })
      }
    });
  }

  getJupyterlabUrl(environment: IEnvironment): string | undefined {
    return environment.services?.find(e => e.name === "Jupyterlab")?.url;
  }

  goToDemos(environment?: IEnvironment) {
    this.gtmService.pushTag({
      event: 'web_launch_demos_success'
    })
    const env = environment ?? this.selectedEnvironment!;
    const url = this.getJupyterlabUrl(env);
    if (url) {
      window.open(url, "_blank");
    }
  }

  isTeraUiReady(environment: IEnvironment): boolean {
    return !!environment.services?.find(
      (service: any) => service.name === 'tera-ui' && service.description === 'Tera UI'
    );
  }

  getProvisioningCountdown(environment: IEnvironment): string | null {
    // Only show countdown for PROVISIONING and INITIALIZING states
    if (!['PROVISIONING', 'INITIALIZING'].includes(environment.state)) {
      return null;
    }

    if (!environment.createDate) {
      return null; // No createDate set yet
    }

    const createdTime = new Date(environment.createDate).getTime();
    const currentTime = this.currentTime.getTime();
    const elapsedMs = currentTime - createdTime;
    const eightMinutesMs = 8 * 60 * 1000; // 8 minutes in milliseconds
    const remainingMs = eightMinutesMs - elapsedMs;

    if (remainingMs <= 0) {
      return null; // Timeout expired
    }

    const remainingSeconds = Math.ceil(remainingMs / 1000);
    const minutes = Math.floor(remainingSeconds / 60);
    const seconds = remainingSeconds % 60;

    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }

  getTeraUiCountdown(environment: IEnvironment): string | null {
    if (this.isTeraUiReady(environment)) {
      return null;
    }

    if (!environment.startedTime) {
      return null; // No startedTime set yet
    }

    const startedTime = new Date(environment.startedTime).getTime();
    const currentTime = this.currentTime.getTime();
    const elapsedMs = currentTime - startedTime;
    const twoMinutesMs = 2 * 60 * 1000; // 2 minutes in milliseconds
    const remainingMs = twoMinutesMs - elapsedMs;

    if (remainingMs <= 0) {
      return null; // Timeout expired
    }

    const remainingSeconds = Math.ceil(remainingMs / 1000);
    const minutes = Math.floor(remainingSeconds / 60);
    const seconds = remainingSeconds % 60;

    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }

  getMcpConfigUrl(environment: IEnvironment): string | undefined {
    // Look for tera-ui service in the environment services
    const teraUiService = environment.services?.find(
      (service: any) => service.name === 'tera-ui' && service.description === 'Tera UI'
    );
    return teraUiService?.url;
  }

  goToMcpConfig(environment?: IEnvironment) {
    const env = environment ?? this.selectedEnvironment!;
    const url = this.getMcpConfigUrl(env);
    if (url) {
      window.open(url, "_blank");
    }
  }

  getVantageOAFUrl(): string | undefined {
    return this.selectedEnvironment!.services.find(e => e.name === "Vantage_OAF")?.url;
  }

  getUESUrl(): string | undefined {
    return this.selectedEnvironment!.services.find(e => e.name === "UES")?.url;
  }

  getVantageOAFCredentials(): any {
    const credentials = this.selectedEnvironment!.services.find(e => e.name === "Vantage_OAF")?.credentials;
    return credentials![0];
  }

  getUESCredentials(): any {
    const credentials = this.selectedEnvironment!.services.find(e => e.name === "UES")?.credentials;
    return credentials![0];
  }

  sendEmail(machineName: string) {
    window.location.href = 'mailto:SC230208@teradata.com?subject=Trial%20Question%20' + machineName;
  }

  getEnvironmentNotifications(environmentName: string): Notification[] {
    return this.notificationsByEnvironment.get(environmentName) ?? [];
  }

  getNotificationIcon(type: string): string {
    return NOTIFICATION_TYPE_ICONS.get(type.toUpperCase()) ?? 'info';
  }

  deleteNotification(notification: Notification) {
    console.log(notification);
    deleteDoc(doc(this.firestore, `users/${this.authService.userData.email}/notifications/${notification.id}`));
  }

  startEnvironmentAndDismiss(environment:IEnvironment,notification: Notification) {
    this.deleteNotification(notification);
    this.startEnvironment(environment.name);
  }

  createEnvironmentAndDismiss(notification: Notification) {
    this.deleteNotification(notification);
    this.showCreateEnvironmentDialog();
  }

  requestHelpAndDismiss(notification: Notification) {
    this.deleteNotification(notification);
    this.sendEmail('general')
  }

  // Carousel methods
  private shouldShowCarousel(): boolean {
    return typeof window !== 'undefined' && window.innerHeight >= this.carouselMinHeightPx;
  }

  loadCarouselItems(): void {
    this.carouselLoading = true;
    const CAROUSEL_URL = 'https://storage.googleapis.com/clearscape-public/carousel/carousel.json';
    
    this.http.get<{ images: { image: string; link: string; type?: 'image' | 'video'; thumbnail?: string }[] }>(CAROUSEL_URL)
      .subscribe({
        next: (data) => {
          this.carouselItems = data.images || [];
          this.carouselLoading = false;
          
          console.log('Loaded carousel items:', this.carouselItems.length);
          
          // Start carousel auto-play after items are loaded
          if (this.showCarousel && this.carouselItems.length > 0) {
            this.startCarousel();
          }
        },
        error: (error) => {
          console.error('Error loading carousel items:', error);
          this.carouselLoading = false;
          // Fallback to empty array
          this.carouselItems = [];
        }
      });
  }

  startCarousel(): void {
    if (!this.showCarousel || this.carouselItems.length === 0) {
      return;
    }
    this.stopCarousel();
    this.carouselInterval = setInterval(() => {
      this.nextCarouselImage();
    }, 5000); // Change image every 5 seconds
  }

  stopCarousel(): void {
    if (this.carouselInterval) {
      clearInterval(this.carouselInterval);
      this.carouselInterval = null;
    }
  }

  nextCarouselImage(): void {
    this.playingCarouselIndex = null;
    this.currentCarouselIndex = (this.currentCarouselIndex + 1) % this.carouselItems.length;
  }

  previousCarouselImage(): void {
    this.playingCarouselIndex = null;
    this.currentCarouselIndex = this.currentCarouselIndex === 0 
      ? this.carouselItems.length - 1 
      : this.currentCarouselIndex - 1;
    this.startCarousel();
  }

  goToCarouselImage(index: number): void {
    this.playingCarouselIndex = null;
    this.currentCarouselIndex = index;
    this.startCarousel();
  }

  onCarouselItemClick(item: { link: string; type?: 'image' | 'video' }, index: number): void {
    // Video ads play inline via the play button; only image ads open the link.
    if (item.type === 'video') {
      return;
    }
    this.openCarouselLink(item.link);
  }

  playCarouselVideo(index: number, event: Event): void {
    event.stopPropagation();
    this.playingCarouselIndex = index;
    // Pause auto-rotation while the video is playing.
    this.stopCarousel();
  }

  expandCarouselVideo(item: { image: string; link: string; type?: 'image' | 'video'; thumbnail?: string }, event: Event): void {
    event.stopPropagation();
    // Stop the inline player so only the expanded view plays.
    this.playingCarouselIndex = null;
    this.expandedVideo = item;
    // Keep auto-rotation paused while the larger view is open.
    this.stopCarousel();
  }

  closeExpandedVideo(): void {
    this.expandedVideo = null;
    if (this.carouselItems.length > 1) {
      this.startCarousel();
    }
  }

  // Cache built embed URLs so the iframe src reference stays stable across change detection.
  private youTubeEmbedCache = new Map<string, SafeResourceUrl>();

  isYouTubeVideo(url: string | undefined | null): boolean {
    return !!url && /(?:youtube\.com|youtu\.be)/i.test(url);
  }

  private extractYouTubeId(url: string): string | null {
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtube\.com\/embed\/|youtu\.be\/|youtube\.com\/shorts\/)([\w-]{11})/,
    ];
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) { return match[1]; }
    }
    return null;
  }

  youTubeEmbedUrl(url: string): SafeResourceUrl | null {
    const id = this.extractYouTubeId(url);
    if (!id) { return null; }
    const cacheKey = `${id}`;
    let embed = this.youTubeEmbedCache.get(cacheKey);
    if (!embed) {
      embed = this.sanitizer.bypassSecurityTrustResourceUrl(
        `https://www.youtube.com/embed/${id}?autoplay=1&rel=0`
      );
      this.youTubeEmbedCache.set(cacheKey, embed);
    }
    return embed;
  }

  onCarouselVideoEnded(): void {
    this.playingCarouselIndex = null;
    // Resume auto-rotation once playback finishes.
    if (this.carouselItems.length > 1) {
      this.startCarousel();
    }
  }

  openCarouselLink(link: string): void {
    window.open(link, '_blank');
  }

  onImageError(event: any, imagePath: string): void {
    console.error('Failed to load image:', imagePath);
    console.error('Error event:', event);
  }

  // Demos methods
  loadDemos(): void {
    this.demosLoading = true;
    this.filtersLoading = true;
    
    // Load demos
    this.jupyterDemosService.getDemos().subscribe({
      next: (demos) => {
        this.allDemos = demos;
        this.filteredDemos = demos;
        this.displayedDemos = demos.slice(0, 10); // Show first 10 demos
        this.demosLoading = false;
      },
      error: (error) => {
        console.error('Error loading demos:', error);
        this.demosLoading = false;
      }
    });

    // Load filter options
    this.jupyterDemosService.getFilters().subscribe({
      next: (categories) => {
        this.subjectFilterConfigs = this.buildSubjectFilterConfigs(categories);
        this.syncFiltersWithSubjectConfigs();
        this.filtersLoading = false;
      },
      error: (error) => {
        console.error('Error loading filters:', error);
        this.filtersLoading = false;
      }
    });
  }

  onFilterChange(): void {
    this.applyFilters();
  }

  onSubjectFilterChange(key: string, value: string): void {
    this.filters[key] = value;
    this.applyFilters();
  }

  loadMoreDemos(): void {
    const currentLength = this.displayedDemos.length;
    const moreDemo = this.filteredDemos.slice(currentLength, currentLength + 10);
    this.displayedDemos = [...this.displayedDemos, ...moreDemo];
  }

  launchDemo(demo: JupyterDemo): void {
    // Check if demo has nbviewer link
    if (!this.hasNbviewerLink(demo)) {
      // Show message that environment is needed
      this.snackBar.open(
        'This notebook is only available in Trial environments. Create an environment to explore it.',
        'Got it',
        { duration: 5000, panelClass: ['info-snackbar'] }
      );
      return;
    }

    const versions = this.getDemoVersions(demo);
    
    if (versions.length === 1) {
      // If only one version, open it inline
      this.openDemoLink(versions[0].url, demo.demo);
    } else if (versions.length > 1) {
      // If multiple versions, show dialog
      this.showDemoVersionsDialog(demo);
    }
  }

  showDemoVersionsDialog(demo: JupyterDemo): void {
    const dialogRef = this.dialog.open(DemoVersionsDialogComponent, {
      width: '500px',
      data: { demo: demo }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.openDemoLink(result, demo.demo);
      }
    });
  }

  openDemoLink(url: string, title: string = 'Notebook Preview'): void {
    this.notebookViewerTitle = title;
    this.notebookViewerUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
    this.isNotebookViewerOpen = true;
  }

  closeNotebookViewer(): void {
    this.isNotebookViewerOpen = false;
    this.notebookViewerUrl = null;
    this.notebookViewerTitle = '';
  }

  getDemoVersions(demo: JupyterDemo): DemoLink[] {
    return demo.links.filter(link => link.type === 'nbviewer');
  }

  hasNbviewerLink(demo: JupyterDemo): boolean {
    return this.jupyterDemosService.hasNbviewerLink(demo);
  }

  // AI Chat methods
  toggleChat(): void {
    this.isChatCollapsed = !this.isChatCollapsed;
  }

  private scrollChatToBottom(): void {
    setTimeout(() => {
      if (this.chatMessagesContainer) {
        const element = this.chatMessagesContainer.nativeElement;
        element.scrollTop = element.scrollHeight;
      }
    }, 100);
  }

  sendChatMessage(): void {
    if (!this.chatMessage?.trim() || this.isChatLoading) {
      return;
    }

    const userMessage = this.chatMessage;
    
    // Add user message to chat
    this.chatMessages.push({
      role: 'user',
      content: userMessage
    });

    // Scroll to bottom after adding user message
    this.scrollChatToBottom();

    // Clear input and set loading state
    this.chatMessage = '';
    this.isChatLoading = true;
    this.chatError = null;

    // Call AI chat service
    this.aiChatService.chat(userMessage, this.chatSessionId || undefined)
      .subscribe({
        next: (response: ChatResponse) => {
          // Store session ID for conversation continuity
          this.chatSessionId = response.session_id;
          
          // Extract notebook names from source documents
          const notebookNames = this.extractNotebookNames(response.source_documents);
          
          // Add AI response to chat (don't append source info to message)
          let aiResponse = response.answer;
          
          // Remove source documents section if it's in the response
          aiResponse = this.removeSourceDocumentsSection(aiResponse);
          
          this.chatMessages.push({
            role: 'assistant',
            content: aiResponse,
            sourceDocuments: notebookNames
          });
          
          this.isChatLoading = false;
          
          // Scroll to bottom after AI response
          this.scrollChatToBottom();
        },
        error: (err) => {
          console.error('Chat error:', err);
          this.chatError = err.message || 'Failed to send message. Please try again.';
          this.isChatLoading = false;
          
          // Show error in snackbar
          this.snackBar.open(this.chatError || 'An error occurred', 'Dismiss', {
            duration: 5000,
            panelClass: ['error-snackbar']
          });
          
          // Remove the user's message if there was an error
          if (this.chatMessages.length > 0 && this.chatMessages[this.chatMessages.length - 1].role === 'user') {
            this.chatMessages.pop();
          }
        }
      });
  }

  clearChatHistory(): void {
    if (!this.chatSessionId) {
      // If no session, just clear local messages
      this.chatMessages = [];
      this.chatError = null;
      
      return;
    }

    // Delete history from server
    this.aiChatService.deleteChatHistory().subscribe({
      next: () => {
        this.chatMessages = [];
        this.chatSessionId = null;
        this.chatError = null;
        
        
        this.snackBar.open('Chat history cleared', 'Dismiss', {
          duration: 3000
        });
      },
      error: (err: any) => {
        console.error('Failed to delete chat history:', err);
        this.snackBar.open('Failed to clear chat history', 'Dismiss', {
          duration: 5000,
          panelClass: ['error-snackbar']
        });
      }
    });
  }

  /**
   * Load chat history from server
   */
  loadChatHistory(): void {
    // Get session ID from user data if available
    this.isChatHistoryLoading = true;
    this.user.subscribe(userData => {
      if (userData && (userData as any).id) {
        const sessionId = (userData as any).id;
        this.chatSessionId = sessionId;
        
        this.aiChatService.getChatHistory().subscribe({
          next: (response: any) => {
            if (response.history && response.history.length > 0) {
              // Load existing history
              this.chatMessages = response.history.map((msg: any) => ({
                role: msg.role,
                content: msg.content,
                sourceDocuments: [] // Source documents not stored in history
              }));
              
              // Scroll to bottom after loading history
              this.scrollChatToBottom();
            }
            this.isChatHistoryLoading = false;
          },
          error: (error: any) => {
            console.error('Error loading chat history:', error);
            this.isChatHistoryLoading = false;
          }
        });
      } else {
        this.isChatHistoryLoading = false;
      }
    });
  }

  /**
   * Format chat message content to display properly with markdown-like formatting
   */
  formatMessage(content: string, sourceDocuments?: string[], messageIndex?: number): SafeHtml {
    let formatted = content;
    
    // Convert emoji codes
    formatted = formatted.replace(/😊/g, '😊');
    
    // Convert markdown headers (## Header -> <h3>Header</h3>)
    formatted = formatted.replace(/^## (.+)$/gm, '<h3 class="chat-heading">$1</h3>');
    formatted = formatted.replace(/^### (.+)$/gm, '<h4 class="chat-subheading">$1</h4>');
    
    // Convert code blocks (```language\ncode\n``` -> <pre><code>code</code></pre>)
    formatted = formatted.replace(/```(\w+)?\n([\s\S]*?)```/g, '<pre class="chat-code"><code>$2</code></pre>');
    
    // Convert inline code (`code` -> <code>code</code>)
    formatted = formatted.replace(/`([^`]+)`/g, '<code class="chat-inline-code">$1</code>');
    
    // Convert bold (**text** -> <strong>text</strong>)
    formatted = formatted.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
    
    // Convert italic (*text* or <i>text</i> -> <em>text</em>)
    formatted = formatted.replace(/\*([^\*]+)\*/g, '<em class="chat-source">$1</em>');
    formatted = formatted.replace(/<i>([^<]+)<\/i>/g, '<em class="chat-source">$1</em>');
    
    // Convert numbered lists (1. item -> <ol><li>item</li></ol>)
    formatted = formatted.replace(/^\d+\.\s+(.+)$/gm, '<li class="chat-list-item">$1</li>');
    if (formatted.includes('<li class="chat-list-item">')) {
      formatted = formatted.replace(/(<li class="chat-list-item">.*<\/li>)/s, '<ol class="chat-ordered-list">$1</ol>');
    }
    
    // Convert line breaks
    formatted = formatted.replace(/\n\n/g, '<br><br>');
    formatted = formatted.replace(/\n/g, '<br>');
    
        // Add filter button at the end if there are source documents
    // Use data attribute and Angular click handler instead of onclick
    if (sourceDocuments && sourceDocuments.length > 0 && messageIndex !== undefined) {
      formatted += `<br><br><span class="inline-filter-link" data-filter-action="true" data-message-index="${messageIndex}">
        <svg class="filter-icon" viewBox="0 0 24 24" width="16" height="16"><path fill="currentColor" d="M10 18h4v-2h-4v2zM3 6v2h18V6H3zm3 7h12v-2H6v2z"/></svg>
        Filter notebook${sourceDocuments.length > 1 ? 's' : ''}
      </span>`;
    }
    
    return this.sanitizer.sanitize(SecurityContext.HTML, formatted) || '';
  }

  /**
   * Extract notebook relative paths from source documents array.
   * Strips the root folder (e.g., "jupyter-demos/") and keeps the relative path.
   * e.g., "jupyter-demos/UseCases/VectorStore/VectorStore_Getting_Started.ipynb"
   *    -> "UseCases/VectorStore/VectorStore_Getting_Started.ipynb"
   * If already a bare name (e.g. "Foo.ipynb"), keeps it as-is.
   */
  private extractNotebookNames(sourceDocuments: string[]): string[] {
    if (!sourceDocuments || sourceDocuments.length === 0) {
      return [];
    }

    return sourceDocuments
      .map(doc => {
        // If doc contains a slash, strip the root folder name (everything before first /)
        if (doc.includes('/')) {
          const firstSlashIndex = doc.indexOf('/');
          return doc.substring(firstSlashIndex + 1);
        }
        // Otherwise return as-is (already a bare filename or relative path)
        return doc;
      })
      .filter((path): path is string => path !== null && path !== '');
  }

  /**
   * Remove source documents section from AI response
   */
  private removeSourceDocumentsSection(content: string): string {
    // Remove various source document patterns
    let cleaned = content;
    
    // Remove "Sources: X document(s)" at the end
    cleaned = cleaned.replace(/\*?Sources:?\s*\d+\s*document\(s\)\*?/gi, '');
    
    // Remove source document lists (both numbered and with <i> tags)
    cleaned = cleaned.replace(/<i>\s*\d+\.\s*[^<]+\.ipynb\s*<\/i>/gi, '');
    cleaned = cleaned.replace(/\d+\.\s*[A-Za-z0-9_-]+\.ipynb/gi, '');
    
    // Remove "Source documents:" header
    cleaned = cleaned.replace(/##?\s*Source\s+documents?:?/gi, '');
    
    // Clean up extra whitespace and line breaks
    cleaned = cleaned.replace(/\n{3,}/g, '\n\n');
    cleaned = cleaned.trim();
    
    return cleaned;
  }

  /**
   * Apply AI filter to demos based on source documents
   */
  applyAiFilter(sourceDocuments: string[]): void {
    console.log('Applying AI filter with notebooks:', sourceDocuments);
    // Extract and normalize notebook names to lowercased stems for matching
    this.aiFilteredNotebooks = this.extractNotebookNames(sourceDocuments);
    console.log('Normalized to stems:', this.aiFilteredNotebooks);
    this.aiFilterActive = true;
    
    // Clear all regular filters when AI filter is applied
    this.resetFilters();
    
    this.applyFilters();
  }

  /**
   * Clear AI filter
   */
  clearAiFilter(): void {
    this.aiFilteredNotebooks = [];
    this.aiFilterActive = false;
    this.applyFilters();
  }

  /**
   * Check if any regular filters (not AI) are active
   */
  hasActiveFilters(): boolean {
    const hasSubjectFilters = this.subjectFilterConfigs.some((filter) => !!this.filters[filter.key]);
    return hasSubjectFilters || !!this.filters['availability'];
  }

  /**
   * Clear all regular filters
   */
  clearAllFilters(): void {
    this.resetFilters();
    this.applyFilters();
  }

  /**
   * Handle click events on message bubbles (for filter link)
   */
  onMessageClick(event: MouseEvent, messageIndex: number): void {
    const target = event.target as HTMLElement;
    
    // Check if clicked element is the filter link or its child (SVG)
    const filterLink = target.closest('.inline-filter-link');
    if (filterLink) {
      event.preventDefault();
      event.stopPropagation();
      
      const message = this.chatMessages[messageIndex];
      if (message?.sourceDocuments && message.sourceDocuments.length > 0) {
        console.log('Filter link clicked for message', messageIndex, 'with sources:', message.sourceDocuments);
        this.applyAiFilter(message.sourceDocuments);
      }
    }
  }

  /**
   * Apply all filters including AI filter
   */
  private applyFilters(): void {
    let filtered = this.allDemos;

    // Apply AI filter first if active
    if (this.aiFilterActive && this.aiFilteredNotebooks.length > 0) {
      console.log('AI Filter Active. Filtering by paths:', this.aiFilteredNotebooks);
      console.log('Total demos before filter:', filtered.length);
      
      // Log first few demo URLs for debugging
      if (filtered.length > 0) {
        console.log('Sample demo URLs:');
        filtered.slice(0, 3).forEach(demo => {
          console.log(`  Demo: "${demo.demo}" has ${demo.links.length} links:`);
          demo.links.forEach(link => console.log(`    - ${link.url}`));
        });
      }

      filtered = filtered.filter(demo => {
        // Check if any of the demo's links contain the notebook relative path in the URL
        const demoMatches = this.aiFilteredNotebooks.some(notebookPath => {
          return demo.links.some(link => {
            // Extract the filename from the notebook path for partial matching
            const notebookFilename = notebookPath.split('/').pop() || notebookPath;
            
            // Try both full path match and filename-only match
            const fullPathMatch = link.url.includes(notebookPath);
            const filenameMatch = link.url.includes(notebookFilename);
            
            const matches = fullPathMatch || filenameMatch;
            if (matches) {
              console.log(`Match found: demo="${demo.demo}" url="${link.url}" path="${notebookPath}" (${fullPathMatch ? 'full path' : 'filename only'})`);
            }
            return matches;
          });
        });

        return demoMatches;
      });

      console.log('Demos after AI filter:', filtered.length);
    }

    // Apply subject filters loaded from filters.json categories.
    for (const filterConfig of this.subjectFilterConfigs) {
      const selectedValue = this.filters[filterConfig.key];
      if (selectedValue) {
        filtered = filtered.filter(demo =>
          demo.subjects?.some(s => s.subject === selectedValue)
        );
      }
    }

    // Apply availability filter
    if (this.filters['availability']) {
      if (this.filters['availability'] === 'preview') {
        // Show only demos with nbviewer links
        filtered = filtered.filter(demo => this.hasNbviewerLink(demo));
      } else if (this.filters['availability'] === 'environment') {
        // Show only demos without nbviewer links (environment required)
        filtered = filtered.filter(demo => !this.hasNbviewerLink(demo));
      }
    }

    this.filteredDemos = filtered;
    this.displayedDemos = filtered.slice(0, 10);
    
    console.log('Final filtered demos:', this.filteredDemos.length);
  }

  private buildSubjectFilterConfigs(categories: FilterCategory[]): FilterDropdownConfig[] {
    return categories.map((category) => ({
      key: category.category,
      label: category.category,
      options: [...category.subjects]
        .sort((a, b) => a.subjseq - b.subjseq)
        .map((subject) => subject.subject)
    }));
  }

  private resetFilters(): void {
    this.subjectFilterConfigs.forEach((filterConfig) => {
      const key = filterConfig.key;
      this.filters[key] = '';
    });
    this.filters['availability'] = '';
  }

  private syncFiltersWithSubjectConfigs(): void {
    const nextFilters: Record<string, string> = {
      availability: this.filters['availability'] || ''
    };

    this.subjectFilterConfigs.forEach((filterConfig) => {
      nextFilters[filterConfig.key] = this.filters[filterConfig.key] || '';
    });

    this.filters = nextFilters;
  }

  private generateMockResponse(message: string): string {
    // Mock responses - replace with actual AI API integration
    const lowerMessage = message.toLowerCase();
    
    if (lowerMessage.includes('environment') || lowerMessage.includes('create')) {
      return "To create a new environment, click the '+' button in the Environments panel. You'll need to provide a name, password, and select a region.";
    } else if (lowerMessage.includes('demo') || lowerMessage.includes('notebook')) {
      return "I can help you find relevant demos! Browse the 'Run dozens of guided demos' section on the right, or tell me what you're interested in (e.g., retail analytics, financial services, machine learning).";
    } else if (lowerMessage.includes('sql') || lowerMessage.includes('query')) {
      return "I can assist with SQL queries! What would you like to know? For example, I can help with SELECT statements, JOIN operations, or advanced analytics functions.";
    } else if (lowerMessage.includes('connect')) {
      return "To connect to your environment, use the Host, Username, and Password shown in your environment details. Check the 'Getting Started Guide' link for step-by-step instructions.";
    } else {
      return "I'm here to help! You can ask me about creating environments, finding demos, writing SQL queries, or connecting to your database.";
    }
  }

  private async checkAndShowProfileCompletion(): Promise<void> {
    try {
      const userEmail = this.authService.userData?.email;
      if (!userEmail) return;

      const isComplete = await this.authService.checkProfileComplete(userEmail.toLowerCase());
      if (!isComplete) {
        this.dialog.open(ProfileCompletionComponent, {
          disableClose: true,
          width: '550px',
          maxWidth: '90vw',
          hasBackdrop: true,
          backdropClass: 'profile-completion-backdrop',
          panelClass: 'profile-completion-dialog-panel'
        });
      }
    } catch (error) {
      console.error('Error checking profile completion:', error);
    }
  }

}

@Component({
  standalone: false,
  selector: 'create-environment-dialog',
  templateUrl: 'create-environment.dialog.html',
  styleUrls: ['./create-environment.dialog.css']
})
export class CreateEnvironmentDialogComponent {
  hide: boolean = true;

  constructor(
    public dialogRef: MatDialogRef<CreateEnvironmentDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: CreateEnvironmentData,
    private gtmService: GoogleTagManagerService,
    private regionDetectionService: RegionDetectionService
  ) { 
    this.initializeRegion();
  }

  onNoClick(): void {
    this.dialogRef.close();
  }

  onSubmit(): void {
    this.gtmService.pushTag({
      event: 'web_create_env_attempt'
    })
    console.log(`onSubmit()`);
    if (this.isFormValid()) {
      console.log('form valid, closing to submit');
      this.dialogRef.close({
        environmentName: this.environmentName.value,
        environmentPassword: this.environmentPassword.value,
        region: this.region.value
      });
    }
  }

  onKeyDown(event: any) {
    if (event.keyCode === 13) {
      this.onSubmit();
    }
  }

  isFormValid(): boolean {
    return !(this.environmentName.value === '' || this.region.invalid || this.environmentName.invalid || this.environmentPassword.invalid);
  }

  environmentName = new FormControl('', [Validators.required, startsWithLetter, containsOnlyAllowedCharacters, doesntEndWithHyphen, Validators.maxLength(40), Validators.pattern('^[a-z]([-a-z0-9]*[a-z0-9])?$')]);
  environmentPassword = new FormControl('', [Validators.required, Validators.pattern('^[a-zA-Z].*$'), Validators.minLength(8)]);
  region = new FormControl('us-central', [Validators.required]);

  /**
   * Initializes the region based on user's location
   */
  private async initializeRegion(): Promise<void> {
    try {
      const detectedRegion = await this.regionDetectionService.detectRegion();
      this.region.setValue(detectedRegion);
    } catch (error) {
      this.region.setValue('us-central');
    }
  }

  getErrorMessageForEnvironmentName() {
    if (this.environmentName.hasError('required')) {
      return 'You must enter a value';
    }
    if (this.environmentName.hasError('startsWithLetter')) {
      return 'Environment name must start with a lowercase letter';
    }
    if (this.environmentName.hasError('containsOnlyAllowedCharacters')) {
      return 'Environment name may contain only lowercase letters, digits and hyphens';
    }
    if (this.environmentName.hasError('doesntEndWithHyphen')) {
      return 'Environment name must not end with a hyphen';
    }
    if (this.environmentName.hasError('pattern')) {
      return 'Environment name must match pattern: [a-z]([-a-z0-9]*[a-z0-9])?';
    }
    if (this.environmentName.hasError('maxlength')) {
      return 'Environment name must have 40 characters or less.';
    }
    return '';
  }

  getErrorMessageForPassword() {
    if (this.environmentPassword.hasError('required')) {
      return 'You must enter a value';
    }
    if (this.environmentPassword.hasError('pattern')) {
      return 'Environment password must start with a letter';
    }
    if (this.environmentPassword.hasError('minlength')) {
      return 'Environment password must have minimum 8 characters';
    }
    return '';
  }

  getErrorMessageForRegion() {
    if (this.region.hasError('required')) {
      return 'You must enter a value';
    }
    return '';
  }
}

@Component({
  standalone: false,
  selector: 'delete-environment-dialog',
  template: `
    <h2 mat-dialog-title>Delete Environment</h2>
    <mat-dialog-content>
      <p>Are you sure you want to delete environment <strong>{{ data.environmentName }}</strong>?</p>
      <p>This action cannot be undone.</p>
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button mat-button (click)="onCancel()">Cancel</button>
      <button mat-flat-button color="warn" (click)="onConfirm()">Delete</button>
    </mat-dialog-actions>
  `
})
export class DeleteEnvironmentDialogComponent {
  constructor(
    public dialogRef: MatDialogRef<DeleteEnvironmentDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { environmentName: string }
  ) {}

  onCancel(): void {
    this.dialogRef.close(false);
  }

  onConfirm(): void {
    this.dialogRef.close(true);
  }
}

@Component({
  standalone: false,
  selector: 'demo-versions-dialog',
  template: `
    <h2 mat-dialog-title>{{ data.demo.demo }}</h2>
    <mat-dialog-content>
      <p class="demo-description">{{ data.demo.description }}</p>
      <p class="select-version-text">Select a version to view:</p>
      <div class="versions-list">
        <button 
          mat-button 
          class="version-item"
          *ngFor="let link of getDemoVersions()"
          (click)="selectVersion(link.url)">
          <mat-icon>open_in_new</mat-icon>
          <span>{{ link.label }}</span>
        </button>
      </div>
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button mat-button (click)="onCancel()">Cancel</button>
    </mat-dialog-actions>
  `,
  styles: [`
    .demo-description {
      color: rgba(0, 0, 0, 0.6);
      font-size: 14px;
      margin-bottom: 20px;
    }
    .select-version-text {
      font-weight: 500;
      margin-bottom: 12px;
      font-size: 14px;
    }
    .versions-list {
      display: flex;
      flex-direction: column;
      gap: 8px;
    }
    .version-item {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 12px 16px;
      text-align: left;
      border: 1px solid #e0e0e0;
      border-radius: 4px;
      transition: all 0.2s ease;
      width: 100%;
      justify-content: flex-start;
    }
    .version-item:hover {
      background-color: #f5f5f5;
      border-color: #4D7FFF;
    }
    .version-item mat-icon {
      color: #4D7FFF;
      font-size: 20px;
      width: 20px;
      height: 20px;
    }
    .version-item span {
      flex: 1;
    }
    mat-dialog-content {
      min-width: 400px;
    }
  `]
})
export class DemoVersionsDialogComponent {
  constructor(
    public dialogRef: MatDialogRef<DemoVersionsDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { demo: JupyterDemo }
  ) {}

  getDemoVersions(): DemoLink[] {
    return this.data.demo.links.filter(link => link.type === 'nbviewer');
  }

  selectVersion(url: string): void {
    this.dialogRef.close(url);
  }

  onCancel(): void {
    this.dialogRef.close();
  }
}
